import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Megaphone, MessageCircle, Send, Users, Sparkles } from "lucide-react";
import { SiteShell } from "@/components/layout/SiteShell";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const TITLE_MAX = 140;
const MESSAGE_MAX = 4000;

export default function AdminCommunityUpdates() {
  const { user } = useAuth();
  const isAdmin = (user?.roles || []).includes("admin");
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [repliesEnabled, setRepliesEnabled] = useState(true);
  const [posting, setPosting] = useState(false);
  const [selectedThreads, setSelectedThreads] = useState({}); // {update_id: [threads]}

  const active = useMemo(() => updates.find((u) => u.active) || null, [updates]);
  const history = useMemo(() => updates.filter((u) => !u.active), [updates]);

  const loadUpdates = async () => {
    try {
      const { data } = await api.get("/admin/community-updates");
      setUpdates(data.updates || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) loadUpdates();
    else setLoading(false);
  }, [isAdmin]);

  const publish = async (e) => {
    e?.preventDefault();
    const t = title.trim();
    const m = message.trim();
    if (!t || !m) {
      toast.error("Title and message are required.");
      return;
    }
    if (!window.confirm(
      active
        ? `Publishing this will replace the current active update "${active.title}". Past updates and reply conversations remain in place. Continue?`
        : "Publish this update to every registered member?",
    )) return;
    setPosting(true);
    try {
      const { data } = await api.post("/admin/community-updates", {
        title: t,
        message: m,
        replies_enabled: repliesEnabled,
      });
      toast.success(`Update published — ${data.notified} members notified.`);
      setTitle("");
      setMessage("");
      setRepliesEnabled(true);
      loadUpdates();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPosting(false);
    }
  };

  const toggleThreads = async (updateId) => {
    if (selectedThreads[updateId]) {
      setSelectedThreads((s) => {
        const cp = { ...s };
        delete cp[updateId];
        return cp;
      });
      return;
    }
    try {
      const { data } = await api.get(`/admin/community-updates/${updateId}/threads`);
      setSelectedThreads((s) => ({ ...s, [updateId]: data.threads || [] }));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (!isAdmin) {
    return (
      <SiteShell>
        <section className="container-page py-16">
          <p className="text-muted-foreground">Admins only.</p>
        </section>
      </SiteShell>
    );
  }

  return (
    <SiteShell>
      <section className="container-page py-10 md:py-14 pb-32 md:pb-24" data-testid="admin-community-updates-root">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <span className="chip"><Sparkles size={14} /> Admin · Community</span>
            <h1 className="mt-3 font-serif text-4xl md:text-5xl tracking-tight">Community Updates</h1>
            <p className="mt-2 text-muted-foreground max-w-2xl">
              Publish one broadcast at a time to every signed-in member. The
              active update replaces the previous one on the dashboard —
              past updates and their reply conversations stay archived here
              and in Messages. All notifications are in-app only.
            </p>
          </div>
        </div>

        {/* Composer */}
        <form
          onSubmit={publish}
          className="mt-8 card-soft space-y-4"
          data-testid="admin-community-update-composer"
        >
          <h2 className="font-serif text-2xl flex items-center gap-2">
            <Megaphone size={20} className="text-[hsl(var(--sb-gold-2))]" />
            {active ? "Post a new update" : "Post the first update"}
          </h2>
          {active && (
            <div className="rounded-xl border border-[hsl(var(--sb-clay)/0.4)] bg-[hsl(var(--sb-clay)/0.08)] p-3 text-sm">
              <strong>Currently live:</strong> "{active.title}" — publishing
              a new update will replace it on the dashboard.
            </div>
          )}
          <div>
            <label className="text-xs uppercase tracking-[0.14em] text-muted-foreground font-semibold">Title</label>
            <input
              className="sb-input mt-1 w-full"
              value={title}
              maxLength={TITLE_MAX}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Payments preview is coming next week"
              data-testid="admin-community-update-title"
            />
            <div className="mt-1 text-xs text-muted-foreground">{title.length}/{TITLE_MAX}</div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.14em] text-muted-foreground font-semibold">Message</label>
            <textarea
              className="sb-input mt-1 w-full min-h-[160px] resize-y leading-relaxed"
              value={message}
              maxLength={MESSAGE_MAX}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Share news, thanks, or a heads-up. This appears on every signed-in dashboard until the next update replaces it."
              data-testid="admin-community-update-message"
            />
            <div className="mt-1 text-xs text-muted-foreground">{message.length}/{MESSAGE_MAX}</div>
          </div>
          <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              className="w-4 h-4"
              checked={repliesEnabled}
              onChange={(e) => setRepliesEnabled(e.target.checked)}
              data-testid="admin-community-update-replies-toggle"
            />
            Allow members to reply privately (recommended)
          </label>
          <div className="flex justify-end">
            <button
              type="submit"
              className="btn-primary"
              disabled={posting || !title.trim() || !message.trim()}
              data-testid="admin-community-update-publish"
            >
              <Send size={16} /> {posting ? "Publishing…" : (active ? "Publish new update" : "Publish update")}
            </button>
          </div>
        </form>

        {/* History */}
        <div className="mt-10">
          <h2 className="font-serif text-2xl flex items-center gap-2">
            <MessageCircle size={20} className="text-[hsl(var(--sb-forest-2))]" />
            Update history
          </h2>
          {loading ? (
            <p className="mt-4 text-sm text-muted-foreground">Loading…</p>
          ) : updates.length === 0 ? (
            <p className="mt-4 text-sm text-muted-foreground">No updates published yet.</p>
          ) : (
            <ul className="mt-4 space-y-4" data-testid="admin-community-updates-list">
              {[...(active ? [active] : []), ...history].map((u) => (
                <li
                  key={u.id}
                  className={`rounded-2xl border p-5 ${
                    u.active
                      ? "border-[hsl(var(--sb-gold)/0.5)] bg-[hsl(var(--sb-cream-2)/0.5)]"
                      : "border-border bg-card"
                  }`}
                  data-testid={`admin-community-update-row-${u.id}`}
                >
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        {u.active && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-[hsl(var(--sb-forest))] text-white text-[10px] font-semibold uppercase tracking-[0.14em] px-2 py-0.5">
                            Live
                          </span>
                        )}
                        <h3 className="font-serif text-xl">{u.title}</h3>
                        {!u.replies_enabled && (
                          <span className="chip text-xs">Replies off</span>
                        )}
                      </div>
                      <p className="mt-2 text-sm whitespace-pre-wrap text-foreground/85">{u.message}</p>
                      <div className="mt-3 flex items-center gap-4 text-xs text-muted-foreground">
                        <span>{new Date(u.date_posted).toLocaleString()}</span>
                        <span className="inline-flex items-center gap-1">
                          <Users size={12} /> {u.reply_count || 0} reply thread{(u.reply_count || 0) === 1 ? "" : "s"}
                        </span>
                      </div>
                    </div>
                    {(u.reply_count || 0) > 0 && (
                      <button
                        type="button"
                        onClick={() => toggleThreads(u.id)}
                        className="btn-ghost border text-xs"
                        data-testid={`admin-community-update-toggle-threads-${u.id}`}
                      >
                        {selectedThreads[u.id] ? "Hide replies" : "Show replies"}
                      </button>
                    )}
                  </div>

                  {selectedThreads[u.id] && (
                    <div className="mt-4 border-t pt-4 space-y-2">
                      {selectedThreads[u.id].length === 0 ? (
                        <p className="text-sm text-muted-foreground">No replies yet.</p>
                      ) : (
                        selectedThreads[u.id].map((t) => (
                          <Link
                            key={t.id}
                            to={`/messages/${t.id}`}
                            className="flex items-center gap-3 rounded-xl border bg-background hover:bg-muted/40 p-3 transition-colors"
                            data-testid={`admin-community-thread-link-${t.id}`}
                          >
                            {t.participant?.avatar_url ? (
                              <img src={t.participant.avatar_url} alt="" className="w-9 h-9 rounded-full object-cover" />
                            ) : (
                              <div className="w-9 h-9 rounded-full bg-[hsl(var(--sb-moss))] grid place-items-center text-[hsl(var(--sb-forest-2))] font-semibold">
                                {(t.participant?.display_name || "?")[0]?.toUpperCase()}
                              </div>
                            )}
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 min-w-0">
                                <span className="font-medium truncate">{t.participant?.display_name || "Member"}</span>
                                {t.unread > 0 && (
                                  <span className="shrink-0 rounded-full bg-[hsl(var(--sb-clay))] text-white text-[10px] px-2 py-0.5">
                                    {t.unread}
                                  </span>
                                )}
                              </div>
                              <div className="text-xs text-muted-foreground truncate">{t.last_message || "New conversation"}</div>
                            </div>
                            <span className="text-xs text-muted-foreground shrink-0">
                              {t.last_message_at ? new Date(t.last_message_at).toLocaleDateString() : ""}
                            </span>
                          </Link>
                        ))
                      )}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>
    </SiteShell>
  );
}
